import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X, type LucideIcon } from 'lucide-react';
import { Button, type ButtonVariant } from './Button';

/**
 * Styled replacement for window.confirm(). The native browser dialog
 * is plain and breaks the app's visual rhythm — this matches the rest
 * of the modal patterns (RaiseFlagModal, AssetPickerModal, etc).
 *
 * Two flavours, picked by `tone`:
 *   warning     — amber tile + amber confirm button (reversible action)
 *   destructive — red tile + red confirm button (irreversible)
 * Default is plain neutral for general confirms.
 *
 * Optional `reasonLabel` adds a free-text input above the buttons; the
 * caller receives the value on confirm. Used by ArrivalUndone where
 * the operator can note "wrong Jim" alongside the corrective event.
 */

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  /** Inline alongside the title — overrides the default icon for the tone. */
  icon?: LucideIcon;
  tone?: 'neutral' | 'warning' | 'destructive';
  confirmLabel?: string;
  cancelLabel?: string;
  /** When set, renders a textarea and passes its value to onConfirm. */
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Pass an async function — modal shows a loading state on the confirm button. */
  onConfirm: (reason?: string) => void | Promise<void>;
  onClose: () => void;
}

interface ToneStyle {
  headerBg: string;
  headerText: string;
  iconColor: string;
  iconTileBg: string;
  buttonVariant: ButtonVariant;
  iconDefault: LucideIcon;
}

const TONE_STYLES: Record<NonNullable<ConfirmModalProps['tone']>, ToneStyle> = {
  neutral: {
    headerBg: 'bg-surface-2',
    headerText: 'text-fg',
    iconColor: 'text-accent',
    iconTileBg: 'bg-accent/10',
    buttonVariant: 'primary',
    iconDefault: AlertTriangle,
  },
  warning: {
    headerBg: 'bg-warning-soft/15',
    headerText: 'text-warning-soft-fg',
    iconColor: 'text-warning',
    iconTileBg: 'bg-warning/15',
    buttonVariant: 'warning',
    iconDefault: AlertTriangle,
  },
  destructive: {
    headerBg: 'bg-danger-soft/15',
    headerText: 'text-danger-soft-fg',
    iconColor: 'text-danger',
    iconTileBg: 'bg-danger/15',
    buttonVariant: 'destructive',
    iconDefault: AlertTriangle,
  },
};

export function ConfirmModal({
  open,
  title,
  message,
  icon,
  tone = 'neutral',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  reasonLabel,
  reasonPlaceholder,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // The keydown handler below registers once per open (deps [open]), so
  // it would close over the busy value from when the modal opened
  // (always false) and Escape could dismiss the dialog while the
  // destructive onConfirm is still in flight. Read through a ref.
  const busyRef = useRef(false);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setBusy(false);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busyRef.current) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the confirm button so Enter confirms; if a reason input is
    // present that takes precedence (autofocus on the textarea).
    setTimeout(() => {
      if (!reasonLabel) confirmRef.current?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const t = TONE_STYLES[tone];
  const Icon = icon ?? t.iconDefault;

  async function run() {
    setBusy(true);
    try {
      await onConfirm(reasonLabel ? reason.trim() || undefined : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        disabled={busy}
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex w-full max-w-md flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className={`flex items-start justify-between gap-3 px-5 py-4 ${t.headerBg} ${t.headerText}`}>
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-token-sm ${t.iconTileBg} ${t.iconColor}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <h2 id="confirm-title" className="mt-1 text-base font-semibold tracking-tight">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded-md p-1 text-fg-muted transition-colors hover:bg-surface/50 hover:text-fg disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {message ? (
          <div className="px-5 py-4 text-sm text-fg-muted">{message}</div>
        ) : null}

        {reasonLabel ? (
          <label className="block px-5 pb-4 text-sm">
            <span className="mb-1 block text-xs font-medium text-fg">
              {reasonLabel}{' '}
              <span className="font-normal text-fg-dim">(optional)</span>
            </span>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={2}
              className="block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>
        ) : null}

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={t.buttonVariant}
            size="sm"
            loading={busy}
            onClick={run}
          >
            {confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}
